import {isNullOrEmpty, NamedValue} from "@2d-front-monorepo/core";
import {JSX, SyntheticEvent, useCallback, useEffect} from "react";
import {useControlId} from "../helpers/useControlId";
import Select from "../HtmlControls/Select";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {MvvmControlProps} from "./MvvmControlProps";
import {useOptionItemMap} from "./useItemMap";
import {useMvvmSingleSelection} from "./useMvvmSelection";
import {useRegisterValidation} from "./useRegisterValidation";
import {unwrapMvvmContext} from "./useMvvmTracking";

export interface MvvmSelectProps<T extends object, TValue> extends MvvmControlProps<T, TValue> {
	items: NamedValue<TValue>[];
}

export default function MvvmSelect<T extends object = never, TValue = never>(props: MvvmSelectProps<T, TValue>): JSX.Element {
	const {
		context: contextProp,
		property,
		id,
		label,
		hint,
		column,
		hidden,
		disabled,
		inlineError,
		validation,
		items,
		placeholder,
		onChange,
		onBlur
	} = props;

	// Controls read the raw context; see unwrapMvvmContext.
	const context = unwrapMvvmContext(contextProp);
	const controlId = useControlId(id);
	const itemMap = useOptionItemMap(items);
	const isRequired = validation?.required != null;

	const {selectedValues, onChangeInternal} = useMvvmSingleSelection(context, property, items, onChange);
	const selectedAsString = selectedValues[0];

	let hasPlaceholder = !isNullOrEmpty(placeholder);
	let finalPlaceholder = placeholder;

	if (isRequired && hasPlaceholder && !isNullOrEmpty(selectedAsString)) {
		//Si es requerido y el valor seleccionado está dentro de los existentes
		//sacar el placeholder (no tiene sentido ya que no se podría seleccionar nunca).
		hasPlaceholder = false;
		finalPlaceholder = undefined;
	}

	// Option values are the item indexes useOptionItemMap assigned (the placeholder option is ""),
	// so the picked index comes straight off the event — no placeholder offset needed.
	const onSelectChange = useCallback(function (e: SyntheticEvent<HTMLSelectElement>): void {
		const index = Number.parseInt(e.currentTarget.value, 10);
		onChangeInternal(Number.isNaN(index) ? [] : [index]);
	}, [onChangeInternal]);

	// Without a placeholder the native <select> visually selects the first option, but no
	// change event fires — so the model would stay empty and required validation would
	// reject a value the user already sees as picked. Sync model to the visible option.
	// CAVEAT: this also fires when the model holds a value that is NOT in `items` (e.g. a
	// stale value, or items still loading) and silently overwrites it with items[0] — which
	// matches what the user sees, but callers loading items asynchronously should pass an
	// empty `items` array until the data is ready.
	useEffect(() => {
		if (hasPlaceholder || items.length === 0 || !isNullOrEmpty(selectedAsString)) {
			return;
		}
		context.setValue(property, items[0].value);
	}, [hasPlaceholder, items, selectedAsString, context, property]);

	useRegisterValidation(context, property, controlId, hidden, validation);
	useMvvmFieldSubscription(context, property);

	return <Select label={label}
	               name={property}
	               items={itemMap}
	               value={selectedAsString}
	               placeholder={finalPlaceholder}
	               hint={hint}
	               hidden={hidden}
	               column={column}
	               id={id}
	               required={isRequired}
	               error={context.getError(property)?.error}
	               inlineError={inlineError}
	               disabled={disabled}
	               onChange={onSelectChange}
	               onBlur={onBlur}
	/>;
};
